// Ranking de Consultores — extraído de AnalyticsComercialView.tsx pra ficar testável fora do
// componente. Regras:
// - leads/conversao vêm de coorte (quem entrou no período); assinados/aprovados/volumes vêm
//   das listas por evento (data da assinatura/aprovação) — um consultor pode aparecer com
//   aprovação de um lead criado em mês anterior.
// - Só entra no ranking quem tem valor > 0 no critério ATUALMENTE selecionado — evita listar
//   quem teve a carteira toda reprovada aparecendo com barra zerada ao ordenar por "kWh
//   aprovado" (ex.: assinou o termo, mas a análise cadastral reprovou tudo depois).
// - O pseudo-grupo "sem responsável" nunca aparece no ranking.
import { isEffectivelyAtOrAfter, fmtKwh, fmtNum, fmtPct } from '../../../src/lib/funnelUtils.ts';
import type { NegRow, RankingSortKey } from './types.ts';

export interface RankingEntry {
    id: string;
    leads: number;
    aprovadosCoorte: number;
    aprovados: number;
    assinados: number;
    volumeAprovado: number;
    volumeAssinado: number;
    conversao: number;
}

export const rankingKeyValue = (r: RankingEntry, key: RankingSortKey): number => {
    switch (key) {
        case 'kwh_aprovado': return r.volumeAprovado;
        case 'qtd_aprovados': return r.aprovados;
        case 'kwh_assinado': return r.volumeAssinado;
        case 'qtd_assinados': return r.assinados;
        case 'conversao': return r.conversao;
    }
};

/** Formatação por critério — reusada pela barra de progresso, pelo badge do card e pelo resumo copiado. */
export const RANKING_VALUE_FORMAT: Record<RankingSortKey, (v: number) => string> = {
    kwh_aprovado: fmtKwh,
    qtd_aprovados: fmtNum,
    kwh_assinado: fmtKwh,
    qtd_assinados: fmtNum,
    conversao: fmtPct,
};

/** Só precisa dos 2 campos usados na agregação por volume — permite passar tanto `NegRow[]`
 * quanto `EventFunnelRow[]` (retorno de `computeEventFunnel`), que não tem `stage` e cujo
 * `consumo_medio_kwh` é opcional. */
interface VolumeRow {
    responsavel_id?: string | null;
    consumo_medio_kwh?: number;
}

export function computeRankingConsultores(
    filtered: NegRow[],
    assinadosEventoRows: VolumeRow[],
    aprovadosEventoRows: VolumeRow[],
    rankingSortKey: RankingSortKey,
): RankingEntry[] {
    const porResp: Record<string, Omit<RankingEntry, 'id' | 'conversao'>> = {};
    const respAgg = (id: string) =>
        porResp[id] || (porResp[id] = { leads: 0, aprovadosCoorte: 0, aprovados: 0, assinados: 0, volumeAprovado: 0, volumeAssinado: 0 });

    filtered.forEach(r => {
        const agg = respAgg(String(r.responsavel_id || 'sem'));
        agg.leads++;
        if (r.status !== 'perdido' && isEffectivelyAtOrAfter(r, 'termo_aprovado')) agg.aprovadosCoorte++;
    });
    assinadosEventoRows.forEach(r => {
        const agg = respAgg(String(r.responsavel_id || 'sem'));
        agg.assinados++;
        agg.volumeAssinado += r.consumo_medio_kwh || 0;
    });
    aprovadosEventoRows.forEach(r => {
        const agg = respAgg(String(r.responsavel_id || 'sem'));
        agg.aprovados++;
        agg.volumeAprovado += r.consumo_medio_kwh || 0;
    });

    return Object.entries(porResp)
        .filter(([id]) => id !== 'sem')
        .map(([id, v]) => ({ id, ...v, conversao: v.leads > 0 ? (v.aprovadosCoorte / v.leads) * 100 : 0 }))
        .filter(r => rankingKeyValue(r, rankingSortKey) > 0)
        .sort((a, b) =>
            rankingKeyValue(b, rankingSortKey) - rankingKeyValue(a, rankingSortKey) ||
            b.volumeAprovado - a.volumeAprovado ||
            b.aprovados - a.aprovados ||
            b.conversao - a.conversao ||
            b.assinados - a.assinados
        )
        .slice(0, 8);
}
